/**
 * MockFeedbackSection - Development tools for generating test feedback
 */
import React, { useState } from 'react';
import { useApiService } from '../../services/apiService';
import { Button } from '../atoms/Button';

export const MockFeedbackSection: React.FC = () => {
  const apiService = useApiService();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  const handleGenerateSingle = async () => {
    setLoading(true);
    setResult(null);
    setIsError(false);
    
    try {
      await apiService.generateMockFeedback(1);
      setResult(`✅ Generated 1 mock feedback item successfully!`);
    } catch (error) {
      setResult(`❌ Failed to generate feedback: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBatch = async () => {
    setLoading(true);
    setResult(null);
    setIsError(false);
    
    try {
      const response = await apiService.generateMockFeedback(5);
      const data = response.data;
      setResult(`✅ Generated ${data.successful || 5} mock feedback items successfully!`);
    } catch (error) {
      setResult(`❌ Failed to generate batch feedback: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTestWebSocket = async () => {
    setLoading(true);
    setResult(null);
    setIsError(false);
    
    try {
      const response = await apiService.testWebSocket();
      const data = response.data;
      setResult(`🔗 WebSocket test sent to ${data.recipients || 0} connected clients!`);
    } catch (error) {
      setResult(`❌ WebSocket test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        🧪 Mock Feedback Testing
      </h3>
      
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Generate realistic test feedback for development and testing. All mock feedback is created with the configured test user and app data.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Button
          onClick={handleGenerateSingle}
          disabled={loading}
          variant="primary"
          size="sm"
          className="w-full"
        >
          {loading ? '⏳ Generating...' : '📝 Generate 1 Feedback'}
        </Button>

        <Button
          onClick={handleGenerateBatch}
          disabled={loading}
          variant="secondary"
          size="sm"
          className="w-full"
        >
          {loading ? '⏳ Generating...' : '📚 Generate 5 Feedback'}
        </Button>

        <Button
          onClick={handleTestWebSocket}
          disabled={loading}
          variant="outline"
          size="sm"
          className="w-full"
        >
          {loading ? '⏳ Testing...' : '🔗 Test WebSocket'}
        </Button>
      </div>

      {result && (
        <div className={`p-4 rounded-lg text-sm ${
          isError 
            ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' 
            : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
        }`}>
          {result}
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        <p><strong>Test Configuration:</strong></p>
        <p>• User: testuser@crossmoore.io (David Test)</p>
        <p>• App: ping-frontend SPA</p>
        <p>• Mock feedback includes realistic metadata and categories</p>
      </div>
    </div>
  );
};

export default MockFeedbackSection;